import React from 'react';
import { Row, Col, Divider, Button, Tooltip, Icon, InputNumber, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import ListRulesComponent from './ListRulesComponent';
import InvestmentManagerComponent from './InvestmentManagerComponent';
import AudienceImpactsComponent from './AudienceImpactsComponent';
import ContentSpecificationAlertComponent from './supporting/ContentSpecificationAlertComponent';
import NumberFormat from '../../../../shared/components/NumberFormat';
import { SeparateNumberFormat, validateIntegerInput, getDomain } from '../../../../../services/utils';
import { checkIsNewDisplayInEdit } from '../../../../../services/campaignManager';
import {
  getMultiplyByStatus,
  getImpressionsPerDay,
  getAmountAudienceDisplay
} from '../../../../../services/display';
import { distribution, impressions as impressionsMath } from '../../services/investmentCalculations';
import Colors from '../../../../../themes/Colors';

const domain = getDomain(window.location.hostname);
// Same ceiling the per-relation impressions input uses. Without it a pasted
// number blows the group total, the per-day chip and the investment out of
// their columns.
const MAX_IMPRESSIONS = 999999999;
/**
 * Header for each group of screens in the Collapse panel of ScreensGroupComponent.
 * Extracted from the headerPanel() function of the original component.
 */
const DisplayGroupHeader = ({
  groupDisplay,
  index,
  contentsDisplay,
  investmentGroup,
  // flags
  programmatic,
  enabledRenderRules,
  isPaymentStatusApproved,
  creationType,
  editCampaignActive,
  currentCampaign,
  // handlers
  stopPropagation,
  showModalGroupDisplayRules,
  groupToggleFillScreen,
  setIndexGroup,
  showModalDeleteRelation,
  fixedBudgetInDisplay,
  handleChangeGroupProposal,
  updateImpressionsDisplay,
  // data
  date_from,
  date_to,
  displaysGroups,
  currency,
  campaignCurrency,
  audience,
  audienceHoursOfDay,
  audienceDaysOfWeek,
  getAudienceHoursOfDayRequest,
  getAudienceDaysOfWeekRequest,
  getTooltipForFixedBudget,
  getContentDisplayByScreenGroup,
  getAvgCPM,
  parentGroupInvestment,
  toggleSecuredInvestment,
  managerDisplaySelector,
  initialBackup,
  hasInitialBackup
}) => {
  const { t } = useTranslation();
  const { key, displays } = groupDisplay;

  const newDisplaysInGroupCount = editCampaignActive
    ? displays.filter(d => checkIsNewDisplayInEdit(d.id, initialBackup, hasInitialBackup)).length
    : 0;
  const hasNewDisplayInGroup = newDisplaysInGroupCount > 0;

  const companyId = displays.length > 0 ? displays[0].company.id || displays[0].company_id : null;

  const isFixedBudgetDisplay = contentsDisplay.every(item => item.isFixedBudget === true);
  const usedCurrency = editCampaignActive ? campaignCurrency : currency;

  const getMultiplier = display =>
    getMultiplyByStatus(editCampaignActive, currentCampaign, display || {});

  const getDisplayMultiplier = displayId => getMultiplier(displays.find(d => d.id === displayId));

  const totalImpressions = contentsDisplay.reduce((total, relation) => {
    return (
      total +
      impressionsMath.fromSpots(relation.promised_shows, getDisplayMultiplier(relation.displayId))
    );
  }, 0);

  // Reach of the group: the audience side of the audience/impacts pair, same
  // value the (hidden) audience column showed with this icon.
  const groupAudience = displays.reduce(
    (total, display) => total + (getAmountAudienceDisplay(audience, display.id) || 0),
    0
  );

  // Paso en impresiones que garantiza al menos +1 salida en la pantalla de
  // multiplicador más alto. Espejo del step del input de inversión.
  const groupStep = distribution.impressionsStep(displays, { getMultiplier });

  const impressionsPerDay = contentsDisplay.reduce((total, relation) => {
    return (
      total +
      getImpressionsPerDay(relation, date_from, date_to, getDisplayMultiplier(relation.displayId))
    );
  }, 0);

  // Reparte las impresiones escritas en partes iguales entre las pantallas del
  // grupo y las convierte a salidas con el multiplicador de cada una, igual que
  // el input de inversión reparte plata y convierte con el CPM.
  const handleImpressionsChange = value => {
    const parseValue = validateIntegerInput(value);
    if (parseValue === null) return;

    const updatedContents = distribution.byGroupImpressions(
      Math.min(parseValue, MAX_IMPRESSIONS),
      displays,
      contentsDisplay,
      { getMultiplier }
    );

    updatedContents.forEach((content, index) => {
      if (content.promised_shows === contentsDisplay[index].promised_shows) return;
      updateImpressionsDisplay(content.displayId, content.relationId, content.promised_shows);
    });
  };

  const isProgrammatic = programmatic === 'programmatic';

  // Spans responsive del header del grupo (grilla de 24).
  // >= 992px: una sola fila (programática: 3 + 4 + 5 + 6 + 6 = 24).
  // 768-991px: 3 columnas por fila. < 768px: 2 columnas.
  const spans = {
    name: { xs: 12, sm: 12, md: 8, lg: 3, xl: 3 },
    rules: { xs: 12, sm: 12, md: 8, lg: 4, xl: 4 },
    actions: isProgrammatic
      ? { xs: 24, sm: 24, md: 8, lg: 5, xl: 5 }
      : { xs: 24, sm: 24, md: 8, lg: 12, xl: 12 },
    impressions: { xs: 12, sm: 12, md: 8, lg: 6, xl: 6 },
    investment: { xs: 12, sm: 12, md: 8, lg: 6, xl: 6 },
    audience: { xs: 24, sm: 24, md: 8, lg: 4, xl: 4 }
  };

  return (
    <Row type="flex" className="displayGroupHeader">
      {/* Nombre del grupo */}
      <Col {...spans.name} className="col-displays-header">
        <span>
          <div className="textGroupColor">
            {key}
          </div>
          <div className="content-info" style={domain === "dv360" ? { color: Colors.primary, background: Colors.extraLightPrimary } : {}}>
            {displays.length} {displays.length === 1 ? t('display') : t('displays')}
          </div>
          {hasNewDisplayInGroup && isPaymentStatusApproved && (
            <div style={{ marginTop: '6px' }}>
              <Tooltip title={t('New screen in this group')}>
                <Tag color="orange">
                  {newDisplaysInGroupCount}{' '}
                  {newDisplaysInGroupCount === 1 ? t('New screen').toLowerCase() : t('New screens').toLowerCase()}
                </Tag>
              </Tooltip>
            </div>
          )}
          {programmatic === 'programmatic' && contentsDisplay.some(c => c.is_secured_investment) && (
            <Tooltip title={t('This group has screens with secured investment.')}>
              <div style={{ textAlign: 'center', marginTop: '8px' }} onClick={e => e.stopPropagation()}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65.5 87.97" style={{ width: '18px', height: '18px' }}>
                  <path fill="#979696" d="M59.7,31.94c0-14.42-11.69-26.11-26.11-26.11S7.48,17.52,7.48,31.94c0,8.37,3.94,15.81,10.06,20.59l-7.69,24.13,11.65-5.25,7.93,9.27,4.64-21.81,4.64,21.81,8.09-9.12,11.5,5.1-7.88-24.74c5.68-4.79,9.3-11.96,9.3-19.97ZM12.77,31.94c0-11.5,9.32-20.82,20.82-20.82s20.82,9.32,20.82,20.82-9.32,20.82-20.82,20.82-20.82-9.32-20.82-20.82Z"/>
                  <circle fill="#979696" cx="33.59" cy="31.94" r="12.81"/>
                </svg>
              </div>
            </Tooltip>
          )}
          <div style={{ textAlign: 'center', paddingTop: '15px', paddingRight: '10px' }}>
            <ContentSpecificationAlertComponent
              displays={displays}
              customContentsFiltered={contentsDisplay}
            />
          </div>
        </span>
        <Divider
          type="vertical"
          style={{ color: Colors.black, height: '10em' }}
          className="hide-on-mobile"
        />
      </Col>

      {/* Reglas del grupo */}
      {enabledRenderRules && (
        <Col {...spans.rules} className="col-displays-header">
          <span>
            <ListRulesComponent
              listContentDisplay={getContentDisplayByScreenGroup(displays)}
              styleRules={{ container: { justifyContent: 'center' } }}
              type="group"
              defaultText={t('Without rules in group')}
              showModalRules={() => showModalGroupDisplayRules(key)}
            />
          </span>
          <Divider
            type="vertical"
            style={{ color: Colors.black, height: '10em' }}
            className="hide-on-mobile"
          />
        </Col>
      )}

      {/* Acciones del grupo */}
      <Col {...spans.actions} className="col-displays-header">
        <span className="groupActions">
          {((creationType !== 'makeProposal' && !editCampaignActive) || editCampaignActive) && (
            <Tooltip title={t('Add contents to screen group')}>
              <Button
                className="buttonActionsGroup"
                shape="circle"
                onClick={e => {
                  stopPropagation(e);
                  setIndexGroup(index);
                }}
              >
                <Icon type="plus" />
              </Button>
            </Tooltip>
          )}
          <Tooltip
            title={!groupDisplay.fill_screen ? t('Expand ad') : t('Return original resolution')}
          >
            <Button
              className="buttonActionsGroup"
              shape="circle"
              onClick={e => {
                stopPropagation(e);
                groupToggleFillScreen(groupDisplay);
              }}
            >
              {!groupDisplay.fill_screen ? (
                <Icon type="fullscreen" />
              ) : (
                <Icon type="fullscreen-exit" />
              )}
            </Button>
          </Tooltip>
          {enabledRenderRules && (
            <Tooltip title={t('Set up rules in group of displays')}>
              <Button
                shape="circle"
                className="buttonActionsGroup"
                onClick={e => {
                  stopPropagation(e);
                  showModalGroupDisplayRules(key);
                }}
              >
                <Icon type="control" />
              </Button>
            </Tooltip>
          )}
          <Tooltip
            placement="top"
            title={
              displaysGroups.length < 2
                ? t('To delete need at least two group in the campaign')
                : t('Remove group')
            }
          >
            <Button
              shape="circle"
              className="buttonActionsGroup"
              disabled={displaysGroups.length < 2}
              onClick={e => {
                showModalDeleteRelation('deleteGroupDisplays', displays);
                stopPropagation(e);
              }}
            >
              <Icon type="close" />
            </Button>
          </Tooltip>
          {programmatic === 'programmatic' && !isPaymentStatusApproved && (
            <Tooltip
              placement="top"
              title={getTooltipForFixedBudget('groupDisplay', isFixedBudgetDisplay)}
            >
              <Button
                shape="circle"
                className="buttonActionsGroup"
                onClick={e => {
                  fixedBudgetInDisplay(displays, isFixedBudgetDisplay);
                  stopPropagation(e);
                }}
              >
                <i
                  className={isFixedBudgetDisplay ? 'fa fa-lock' : 'fa fa-unlock'}
                  aria-hidden="true"
                  style={{ color: 'gray', cursor: 'pointer', fontSize: 'large' }}
                />
              </Button>
            </Tooltip>
          )}
        </span>
        <Divider
          type="vertical"
          style={{ color: Colors.black, height: '10em' }}
          className="hide-on-mobile"
        />
      </Col>

      {programmatic === 'programmatic' && (
        <>
          <Col {...spans.impressions} className="col-displays-header">

            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="textGroup">
                {t('Impressions bought')}
                {groupAudience > 0 && (
                  <Tooltip
                    title={
                      <div style={{ textAlign: 'center' }}>
                        <strong style={{ whiteSpace: 'nowrap' }}>
                          {t('Max. available impressions')}
                        </strong>
                        <div>{SeparateNumberFormat(groupAudience)}</div>
                      </div>
                    }
                  >
                    <Icon
                      type="usergroup-add"
                      theme="outlined"
                      style={{ marginLeft: '5px', color: Colors.primaryAccent }}
                    />
                  </Tooltip>
                )}
              </div>
              <div className="textGroup">
                <div
                  style={{ position: 'relative' }}
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <InputNumber
                    style={{ width: '140px' }}
                    formatter={SeparateNumberFormat}
                    parser={v => {
                      const cleaned = String(v).replace(/\D/g, '');
                      return cleaned === '' ? '' : Number(cleaned);
                    }}
                    value={totalImpressions}
                    min={0}
                    max={MAX_IMPRESSIONS}
                    step={groupStep}
                    onChange={handleImpressionsChange}
                    disabled={isPaymentStatusApproved || isFixedBudgetDisplay}
                  />
                </div>
              </div>
              <div className="content-info" style={{ color: Colors.primary, background: Colors.extraLightPrimary }}>
                {SeparateNumberFormat(impressionsPerDay)} {t('impressions/day')}
              </div>
            </span>
            <Divider
              type="vertical"
              style={{ color: Colors.black, height: '10em' }}
              className="hide-on-mobile"
            />
          </Col>

          {/* Last column: keep the content clear of the Collapse arrow. The
              clearance lives in `.col-displays-header-last` (>= 992px only):
              inline it would also eat 40px of this column on phones. */}
          <Col {...spans.investment} className="col-displays-header col-displays-header-last">
            {/* Definite width so the currency input shrinks with the column
                instead of overflowing it. */}
            <span style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
              <div className="textGroup">{t('Investment')}</div>
              <div className="textGroup">
                <InvestmentManagerComponent
                currency={usedCurrency}
                value={investmentGroup}
                onChange={(value, typeOfUpdate) =>
                  handleChangeGroupProposal(
                    value,
                    displays,
                    investmentGroup,
                    typeOfUpdate,
                    parentGroupInvestment,
                    []
                  )
                }
                disabled={isPaymentStatusApproved || isFixedBudgetDisplay}
                parentGroupInvestment={parentGroupInvestment}
                step={Math.max(...displays.map(d => d.cpmi)) * displays.length + 1}
                  tooltip={t(
                    'This indicates what percentage of the total budget allocated to this grouping corresponds to the investment assigned to this group. You can modify it by editing either the investment or directly the percentage.'
                  )}
                />
              </div>
              <div className="content-info" style={{ color: Colors.primary, background: Colors.extraLightPrimary }}>
                <Tooltip placement="right" title={t('(CPM) cost per thousand impression by group')}>
                  <NumberFormat value={getAvgCPM(contentsDisplay)} currency={usedCurrency} />{' '}
                  {t('CPM Average')}
                </Tooltip>
              </div>
            </span>
          </Col>
        </>
      )}

      {/* Audiencia: en programática ya se muestra en el tooltip de impresiones */}
      {programmatic !== 'programmatic' && (
        <Col {...spans.audience} className="col-displays-header col-displays-header-audience">
          <div>
            <div className="textGroup">{t('Audience')}</div>
            <AudienceImpactsComponent
              displays={displays}
              audience={audience}
              audienceHoursOfDay={audienceHoursOfDay}
              audienceDaysOfWeek={audienceDaysOfWeek}
              getAudienceHoursOfDayRequest={getAudienceHoursOfDayRequest}
              getAudienceDaysOfWeekRequest={getAudienceDaysOfWeekRequest}
            />
          </div>
        </Col>
      )}
    </Row>
  );
};

export default DisplayGroupHeader;
